"use client";

import { useEffect } from "react";
import { isModuleLoadError, recoverFromModuleLoadError } from "@/lib/module-load-recovery";
import { pageLoadFailure } from "@/lib/page-route";
import { withSentry } from "@/lib/sentry-browser";
import { StatusPage } from "@/components/status-page";
import { Button } from "@/components/ui/button";

export default function GlobalError({
  error,
  reset,
}: {
  // TanStack Router passes whatever was thrown; the helpers narrow it.
  error: unknown;
  reset?: () => void;
}) {
  useEffect(() => {
    const recovery = recoverFromModuleLoadError(error);
    if (recovery === "reloading") return;
    const failure = pageLoadFailure(error);
    withSentry((sentry) => sentry.captureException(error, {
      tags: {
        "app.error_boundary": "page",
        "app.module_recovery": recovery,
        "app.online": navigator.onLine,
        ...(failure ? { "app.load_stage": failure.stage, "app.route": failure.route } : {}),
      },
    }));
  }, [error]);

  const retry = () => {
    // Router reset reuses a rejected module import; a new document can fetch
    // the current deployment's chunk URLs instead.
    if (isModuleLoadError(error) || !reset) window.location.reload();
    else reset();
  };

  return (
    <StatusPage
      eyebrow="Something went wrong"
      title="Atlas could not load this page"
      description="Please try again. If the problem continues, contact support."
    >
      <Button asChild variant="outline"><a href="/">Go to Atlas</a></Button>
      <Button type="button" onClick={retry}>Try again</Button>
    </StatusPage>
  );
}
